import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/authContext';

//const API_URL = import.meta.env.VITE_API_URL;

const EventHistoryPage = () => {
    const { token } = useContext(AuthContext);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        fetchEvents();
    }, [token]);

    const fetchEvents = async () => {
        try {
            const response = await fetch(`/api/messages/events/history`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al cargar el historial de eventos');
            }

            const data = await response.json();
            setEvents(data.events || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getEventIcon = (type) => {
        const icons = {
            login: '👤',
            user_created: '🆕',
            user_provincial: '🏛️',
            user_municipal: '🏘️',
            user_entidad: '⚽',
            entity_created: '🏢',
            entity_edited: '✏️',
            entity_request_received: '📥',
            request_validated: '✅',
            request_approved: '🎉',
            request_rejected: '❌',
            replace_access: '🔄',
            municipal_request_created: '📝',
            municipal_request_approved: '✅',
            municipal_request_rejected: '❌',
            municipal_request_completed: '✅',
            error: '⚠️',
            default: '📢'
        };
        return icons[type] || icons.default;
    };

    const getEventLabel = (type) => {
        const labels = {
            login: 'Login',
            user_created: 'Usuario Creado',
            user_provincial: 'Usuario Provincial',
            user_municipal: 'Usuario Municipal',
            user_entidad: 'Usuario Entidad',
            entity_created: 'Entidad Creada',
            entity_edited: 'Entidad Editada',
            entity_request_received: 'Solicitud Entidad Recibida',
            request_validated: 'Solicitud Validada',
            request_approved: 'Solicitud Aprobada',
            request_rejected: 'Solicitud Rechazada',
            replace_access: 'Reemplazo de Acceso',
            municipal_request_created: 'Solicitud Municipal Creada',
            municipal_request_approved: 'Solicitud Municipal Aprobada',
            municipal_request_rejected: 'Solicitud Municipal Rechazada',
            municipal_request_completed: 'Registro Municipal Completado',
            error: 'Error'
        };
        return labels[type] || type;
    };

    const filteredEvents = filter === 'all' 
        ? events 
        : events.filter(e => e.type === filter);

    const eventTypes = ['all', ...new Set(events.map(e => e.type))];

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4">
                <p className="text-red-700">{error}</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
                Historial de Eventos del Sistema
            </h1>

            {/* Filters */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Filtrar por tipo:
                </label>
                <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                    <option value="all">Todos los eventos</option>
                    {eventTypes.filter(t => t !== 'all').map(type => (
                        <option key={type} value={type}>
                            {getEventIcon(type)} {getEventLabel(type)}
                        </option>
                    ))}
                </select>
            </div>

            {/* Stats */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-300">Total Eventos</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-300">{events.length}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-300">Filtrados</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-300">{filteredEvents.length}</p>
                </div>
            </div>

            {/* Events Table */}
            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Fecha
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Tipo
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Usuario
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Detalles
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                ID Entidad
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredEvents.map((event) => (
                            <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {new Date(event.created_at).toLocaleString('es-AR')}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                        {getEventIcon(event.type)} {getEventLabel(event.type)}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {event.user_name || '-'}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md truncate">
                                    {event.details || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {event.entity_id || '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredEvents.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    No hay eventos registrados
                </div>
            )}
        </div>
    );
};

export default EventHistoryPage;
